import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ResponsiveModal from "@/components/ui/responsiveModal";
import SpinnerCustom from "@/components/ui/spinner";

import { useCartDetailsMap } from "@/components/cart/hooks/useCartDetailsMap";
import { useCartCardsViewModel } from "@/components/cart/hooks/useCartCardsViewModel";
import {
  changeCartDishQuantityBy,
  removeCartDish,
} from "@/components/cart/services/cartService";
import CartDishCard from "@/components/cart/сartDishCard";
import { useGetCartCurrencyQuery } from "@/graphql/cart/cart.hooks";
import ConfirmDeleteModal from "@/components/cart/confirmDeleteModal";
import CartEmptyState from "@/components/cart/cartEmptyState";
import styles from "@/components/ui/responsiveModal/responsiveModal.module.scss";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";
import { useGetMenuIdQuery } from "@/graphql/menuId/menuId.hooks";

type PendingDelete = { dishId: number } | null;

interface CartModalContainerProps {
  isOpen: boolean;
  onClose: () => void;
  onEditDish: (dishId: number) => void;
  onConfirmDeleteOpen?: () => void;
  onConfirmDeleteClose?: () => void;
}

const CartModalContainer: React.FC<CartModalContainerProps> = ({
  isOpen,
  onClose,
  onEditDish,
  onConfirmDeleteOpen,
  onConfirmDeleteClose,
}) => {
  const { t } = useTranslation();
  const { menuId, branchId, typeMenu } = useParams();
  const menuTypeEnum = getMenuTypeFromUrl(typeMenu);

  const { data: menuIdData } = useGetMenuIdQuery({
    skip: !branchId || !menuTypeEnum,
    variables: {
      branchId: Number(branchId),
      typeMenu: menuTypeEnum,
    },
  });

  const menuData = menuIdData?.menu?.id;
  const menuIdNumber = menuId ? Number(menuId) : Number(menuData);

  const { detailsMap, isLoading, cartItems } = useCartDetailsMap(
    menuIdNumber,
    isOpen
  );
  const hasMenuId = Boolean(menuId);

  const { data: currencyData, loading: currencyLoading } =
    useGetCartCurrencyQuery({
      variables: {
        menuId: Number(menuId),
        branchId: Number(branchId),
        typeMenu: hasMenuId ? null : menuTypeEnum,
      },
      skip: !branchId || !menuTypeEnum,
    });

  const currency: string = currencyData?.menu?.branch?.currencyCode ?? "";

  const { cards, totalText } = useCartCardsViewModel({
    cartItems,
    detailsMap,
    currency,
  });

  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  const requestDelete = useCallback(
    (dishId: number) => {
      setPendingDelete({ dishId });
      onConfirmDeleteOpen?.();
    },
    [onConfirmDeleteOpen]
  );

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
    onConfirmDeleteClose?.();
  }, [onConfirmDeleteClose]);

  const confirmDelete = useCallback(() => {
    if (!menuIdNumber || !pendingDelete) return;

    removeCartDish(menuIdNumber, pendingDelete.dishId);
    setPendingDelete(null);

    onConfirmDeleteClose?.();
  }, [menuIdNumber, pendingDelete, onConfirmDeleteClose]);

  const handleDecrease = useCallback(
    (dishId: number, currentQty: number) => {
      if (!menuIdNumber) return;

      if (currentQty <= 1) {
        requestDelete(dishId);
        return;
      }

      changeCartDishQuantityBy(menuIdNumber, dishId, -1);
    },
    [menuIdNumber, requestDelete]
  );

  const handleIncrease = useCallback(
    (dishId: number) => {
      if (!menuIdNumber) return;
      changeCartDishQuantityBy(menuIdNumber, dishId, 1);
    },
    [menuIdNumber]
  );

  const footer = useMemo(() => {
    return (
      <div className="w-100 d-flex justify-content-between align-items-center m-0">
        <span className="fw-bold fs-20">{totalText}</span>
      </div>
    );
  }, [totalText]);

  return (
    <>
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        headerLeft={<h5 className="m-0">{t("Favorites")}</h5>}
        footer={footer}
        bodyClassName="pt-2 px-4"
        footerClassName={styles.footerShadow}
      >
        {isLoading || currencyLoading ? (
          <div className="d-flex justify-content-center py-5">
            <SpinnerCustom />
          </div>
        ) : cards.length === 0 ? (
          <CartEmptyState />
        ) : (
          <div className="d-flex flex-column gap-4">
            {cards.map((vm) => (
              <CartDishCard
                key={vm.dishId}
                vm={vm}
                onIncrease={() => handleIncrease(vm.dishId)}
                onDecrease={() => handleDecrease(vm.dishId, vm.quantity)}
                onEdit={() => onEditDish(vm.dishId)}
                onDelete={() => requestDelete(vm.dishId)}
              />
            ))}
          </div>
        )}
      </ResponsiveModal>

      {pendingDelete ? (
        <ConfirmDeleteModal
          isOpen={Boolean(pendingDelete)}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
};

export default CartModalContainer;
